import React, { useCallback, useEffect, useState } from 'react'
import { Bookmark, Loader2, AlertCircle, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { cn } from '@/lib/utils'
import { basecampApi } from '@/lib/basecamp-api'
import { testIds } from '@/lib/test-ids'
import { setBookmarksProjectFocus } from '@/lib/basecamp-tab-routing'
import {
  HomeFeedList,
  HomeFeedRow,
  HomeWidgetShell,
} from '@/components/dashboard/home-widget-shell'
import { HomeFeedContextMenu } from '@/components/dashboard/home-feed-context-menu'
import {
  basecampTodoToSelection,
  homeSelectionKey,
  type HomeContinuityProps,
} from '@/components/dashboard/home-selection'
import type { BasecampAssignment } from '@/types/basecamp'

const BOOKMARK_STALE_MS = 30 * 60 * 1000

interface BasecampBookmarksSectionProps {
  limit?: number
  continuity?: HomeContinuityProps
}

function transformBookmarks(items: BasecampAssignment[]): BasecampAssignment[] {
  return (items || []).map((item: any) => ({
    ...item,
    project: item.project || {
      id: item.project_id?.toString() || item.bucket_id?.toString() || '',
      name: item.project_name || item._project_name || 'Unknown Project',
    },
    id: item.id?.toString() || item.basecamp_todo_id?.toString() || '',
  }))
}

function sortAndLimit(items: BasecampAssignment[], limit: number): BasecampAssignment[] {
  return [...items]
    .sort((a, b) => {
      if (!a.due_on && !b.due_on) return 0
      if (!a.due_on) return 1
      if (!b.due_on) return -1
      return new Date(a.due_on).getTime() - new Date(b.due_on).getTime()
    })
    .slice(0, limit)
}

export function BasecampBookmarksSection({
  limit = 5,
  continuity,
}: BasecampBookmarksSectionProps) {
  const [bookmarks, setBookmarks] = useState<BasecampAssignment[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [cachedAt, setCachedAt] = useState<string | null>(null)
  const [stale, setStale] = useState(false)
  const [contextMenu, setContextMenu] = useState<{
    x: number
    y: number
    bookmark: BasecampAssignment
  } | null>(null)

  const applyPayload = useCallback(
    (data: { items?: BasecampAssignment[]; cached_at?: string; stale?: boolean }) => {
      const transformed = transformBookmarks(data.items || [])
      setBookmarks(sortAndLimit(transformed, limit))
      setCachedAt(data.cached_at ?? null)
      setStale(Boolean(data.stale))
    },
    [limit],
  )

  const loadBookmarks = useCallback(
    async (forceRefresh = false) => {
      try {
        if (forceRefresh) {
          setRefreshing(true)
        } else {
          setLoading(true)
        }
        setError(null)
        const data = await basecampApi.getMyBookmarks(forceRefresh)
        applyPayload(data)
        return data
      } catch (err) {
        console.error('[BasecampBookmarksSection] Error loading bookmarks:', err)
        setError(err instanceof Error ? err.message : 'Failed to load bookmarks')
        return null
      } finally {
        setLoading(false)
        setRefreshing(false)
      }
    },
    [applyPayload],
  )

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      const data = await loadBookmarks(false)
      if (cancelled || !data) return
      const cacheAgeMs = data.cached_at
        ? Date.now() - new Date(data.cached_at).getTime()
        : Number.POSITIVE_INFINITY
      const needsRefresh = Boolean(data.stale) || cacheAgeMs > BOOKMARK_STALE_MS
      if (needsRefresh) {
        await loadBookmarks(true)
      }
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [loadBookmarks])

  useEffect(() => {
    if (!contextMenu) return
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setContextMenu(null)
    }
    const handleClick = () => setContextMenu(null)
    document.addEventListener('keydown', handleKey)
    document.addEventListener('mousedown', handleClick)
    return () => {
      document.removeEventListener('keydown', handleKey)
      document.removeEventListener('mousedown', handleClick)
    }
  }, [contextMenu])

  const selectBookmark = (bookmark: BasecampAssignment, opts?: { openPreview?: boolean }) => {
    continuity?.selectHome(basecampTodoToSelection(bookmark), opts)
  }

  const openBookmark = (bookmark: BasecampAssignment) => {
    if (continuity) {
      continuity.openSelection(basecampTodoToSelection(bookmark))
      return
    }
    const tabId = `basecamp-todo-${bookmark.id}`
    const projectId = bookmark.project?.id || bookmark.project_id || bookmark.bucket_id
    if (!projectId) {
      console.error('[BasecampBookmarksSection] Bookmark missing project ID:', bookmark)
      return
    }
    window.dispatchEvent(
      new CustomEvent('integration-view-change', {
        detail: {
          tabId,
          title: bookmark.title,
          type: 'integration-basecamp-todo',
          integrationData: {
            integrationId: 'basecamp',
            projectId: projectId.toString(),
            todoId: bookmark.id.toString(),
            projectName: bookmark.project?.name || bookmark.project_name || 'Unknown Project',
          },
        },
      }),
    )
  }

  const openBookmarksView = () => {
    setBookmarksProjectFocus(null)
    window.dispatchEvent(
      new CustomEvent('integration-view-change', {
        detail: {
          tabId: 'basecamp-bookmarks',
          title: 'Bookmarks',
          type: 'integration-basecamp',
          integrationData: {
            integrationId: 'basecamp',
            section: 'bookmarks',
          },
        },
      }),
    )
  }

  const getDueDateStatus = (dueOn?: string) => {
    if (!dueOn) return null
    const due = new Date(dueOn)
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const dueDate = new Date(due.getFullYear(), due.getMonth(), due.getDate())
    const diffDays = Math.floor((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))

    if (diffDays < 0) {
      return {
        label: `Overdue by ${Math.abs(diffDays)} day${Math.abs(diffDays) > 1 ? 's' : ''}`,
        variant: 'destructive' as const,
      }
    }
    if (diffDays === 0) return { label: 'Due today', variant: 'destructive' as const }
    if (diffDays === 1) return { label: 'Due tomorrow', variant: 'default' as const }
    if (diffDays <= 7) return { label: `Due in ${diffDays} days`, variant: 'default' as const }
    return { label: due.toLocaleDateString(), variant: 'secondary' as const }
  }

  const cacheHint = stale
    ? 'May be stale'
    : cachedAt
      ? `Updated ${new Date(cachedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
      : null

  return (
    <HomeWidgetShell
      title="Basecamp Bookmarks"
      icon={<Bookmark className="fill-muted-foreground/20" />}
      data-testid={testIds.home.basecampBookmarks}
      action={
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void loadBookmarks(true)}
            disabled={loading || refreshing}
            className="h-7 w-7 p-0"
            title="Refresh bookmarks"
            data-testid={testIds.home.basecampBookmarksRefresh}
          >
            <RefreshCw className={cn('h-3.5 w-3.5', (loading || refreshing) && 'animate-spin')} />
          </Button>
          {bookmarks.length > 0 ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={openBookmarksView}
              className="h-7 px-2"
              data-testid={testIds.basecamp.homeBookmarksViewAll}
            >
              View All
            </Button>
          ) : null}
        </div>
      }
      footer={
        bookmarks.length > 0 && cacheHint ? (
          <p className="text-[11px] text-muted-foreground/80">{cacheHint}</p>
        ) : null
      }
    >
      {contextMenu && (
        <HomeFeedContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={() => setContextMenu(null)}
          onOpen={() => openBookmark(contextMenu.bookmark)}
          onPreview={() => selectBookmark(contextMenu.bookmark, { openPreview: true })}
          workspaces={continuity?.workspaces ?? []}
        />
      )}

      {loading && (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      )}

      {error && (
        <Alert variant="destructive" className="bg-destructive/10 border-destructive/50">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="text-sm font-medium">{error}</AlertDescription>
        </Alert>
      )}

      {!loading && !error && bookmarks.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          <p className="text-sm">No bookmarked todos</p>
          <p className="text-xs mt-1">Bookmark a todo from Basecamp to see it here</p>
        </div>
      )}

      {!loading && !error && bookmarks.length > 0 && (
        <HomeFeedList>
          {bookmarks.map((bookmark) => {
            const dueStatus = getDueDateStatus(bookmark.due_on)
            const projectName = bookmark.project?.name || bookmark.project_name || 'Unknown Project'
            const isSelected =
              continuity != null &&
              homeSelectionKey(basecampTodoToSelection(bookmark)) === continuity.selectedKey
            return (
              <HomeFeedRow
                key={bookmark.id}
                title={
                  <span className={cn(bookmark.completed && 'line-through text-muted-foreground')}>
                    {bookmark.title}
                  </span>
                }
                meta={
                  <span className="inline-flex items-center gap-1.5 flex-wrap">
                    <span>{projectName}</span>
                    {dueStatus ? (
                      <Badge variant={dueStatus.variant} className="text-[10px] px-1.5 py-0 h-5 font-normal">
                        {dueStatus.label}
                      </Badge>
                    ) : null}
                    {bookmark.completed ? (
                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-5 font-normal">
                        Completed
                      </Badge>
                    ) : null}
                  </span>
                }
                selected={isSelected}
                disabled={Boolean(bookmark.completed)}
                onClick={(e) => {
                  if (e.metaKey || e.ctrlKey) {
                    selectBookmark(bookmark)
                    openBookmark(bookmark)
                    return
                  }
                  selectBookmark(bookmark)
                }}
                onDoubleClick={() => {
                  selectBookmark(bookmark)
                  openBookmark(bookmark)
                }}
                onContextMenu={(e) => {
                  e.preventDefault()
                  selectBookmark(bookmark)
                  setContextMenu({ x: e.clientX, y: e.clientY, bookmark })
                }}
              />
            )
          })}
        </HomeFeedList>
      )}
    </HomeWidgetShell>
  )
}
